import moment from "moment";
import React from "react";

import { PhosphorLinkBreakButton } from "components/Atoms/PhosphorIconButton";
import { FlightSidePanel } from "components/SidePanel/Flight/FlightSidePanel";
import { TrainSidePanel } from "components/SidePanel/Train/TrainSidePanel";
import { GroundTransportSidePanel } from "components/SidePanel/GroundTransport/GroundSidePanel";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import { AppointmentPhase } from "../../components/";
import { formatTime, is24Hour } from "utilities";

interface Props {
  canLinkUnlinkTravel: boolean
  appointment: server.dto.AppointmentForList
  unlink: (appointmentId: number) => void
}

const formatAirport = (location: string, code: string) => code ? `${location} [${code}]` : location;

export const getLinkedTravelInfo = (appointment: server.dto.AppointmentForList) => {
  switch (appointment.type) {
    case "flight": {
      const flight = appointment.flight;
      return {
        id: flight.flightId,
        base: flight,
        summary: `${formatAirport(flight.departureAirportLocation, flight.departureAirportCode)} » ${formatAirport(flight.arrivalAirportLocation, flight.arrivalAirportCode)}`,
        description: [RESX.Flight.resxFlight, flight.flightDesignator].filter(Boolean).join(" ")
      };
    }
    case "train": {
      const train = appointment.train;
      return {
        id: train.trainId,
        base: train,
        summary: `${train.departureStationName} » ${train.arrivalStationName}`,
        description: [RESX.Train.resxTrain, train.trainNumber].filter(Boolean).join(" ")
      };
    }
    case "groundTransport": {
      const groundTransport = appointment.groundTransport;
      const hasPlaceNames = Boolean(groundTransport.departurePlaceName) || Boolean(groundTransport.arrivalPlaceName);
      return {
        id: groundTransport.groundTransportId,
        base: groundTransport,
        summary: `${groundTransport.departurePlaceType} » ${groundTransport.arrivalPlaceType}`,
        description: hasPlaceNames ? `${groundTransport.departurePlaceName ?? ""} » ${groundTransport.arrivalPlaceName ?? ""}`.trim() : null
      };
    }
    default:
      throw new Error(`Unexpected linked travel type: ${appointment.type}`);
  }
};

const LinkedTravelSidePanel = ({ appointment, children }: { appointment: server.dto.AppointmentForList, children: JSX.Element }) => {
  switch (appointment.type) {
    case "flight":
      return <FlightSidePanel flightId={appointment.flight.flightId} trigger={children} />;
    case "train":
      return <TrainSidePanel trainId={appointment.train.trainId} trigger={children} />;
    case "groundTransport":
      return <GroundTransportSidePanel groundTransportId={appointment.groundTransport.groundTransportId} trigger={children} />;
    default:
      throw new Error(`Unexpected linked travel type: ${appointment.type}`);
  }
};

class LinkedTravelRowCard extends React.Component<Props> {
  unlink = () => {
    const { unlink, appointment } = this.props;
    unlink(getLinkedTravelInfo(appointment).id);
  };

  render(): JSX.Element {
    const {
      appointment,
      canLinkUnlinkTravel
    } = this.props;

    const { base, summary, description } = getLinkedTravelInfo(appointment);

    const startDate = moment.utc(base.dateTime.startDisplay);
    const endDate = moment.utc(base.dateTime.endDisplay);

    const timeDivSize = is24Hour() ? { flex: "0 0 60px" } : { flex: "0 0 80px" };

    return <div>
      <div className="margin-right-small">
        <AppointmentPhase value={base.appointmentPhase} displayLabel={false} />
      </div>
      <div className="flex-column gap grow1">
        <div className="flex gap-small">
          <div className="bigger_text" style={{ flex: "0 0 90px" }}>
            {startDate.format("D MMM YY")}
          </div>

          <div className="big_text" style={timeDivSize}>
            {formatTime(startDate)}
          </div>

          <div>
            <div className="margin-top-mini">
              <i className="icon-arrow-right small"></i>
            </div>
          </div>

          <div className="big_text" style={timeDivSize}>
            <FormatArrivalTime departure={startDate} arrival={endDate} />
          </div>

          <LinkedTravelSidePanel appointment={appointment}>
            <a onClick={() => { }} className="grow1">
              <b>
                {summary}
              </b><br />
              {description}
            </a>
          </LinkedTravelSidePanel>
        </div>

        {base.contactNames && <div className="border-top padding-top s1_gray">
          {base.contactNames}
        </div>}
      </div>

      {canLinkUnlinkTravel && <PhosphorLinkBreakButton
        layout="in-square"
        onClick={this.unlink} />}
    </div>;
  }
}

export default LinkedTravelRowCard;
